// client/src/components/pages/LexiStar/RecomputeJobs/recomputeLogic.js
//
// Pure helpers for recompute jobs and their before/after diff (T4-S12;
// LS-09 §5.19; m3-contracts §3.1 recompute_jobs / recompute_diff_rows):
// state vocabulary, progress, the two-person publish gate (SR-21), headline
// delta cards, diff-row field comparison and the moved arrivals/departures
// the map toggle draws.

import { isMe } from '../ReviewQueue/assuranceShared';
import { fmtDateTime, fmtNum } from '../StatusBoard/format';

export const JOB_STATES = {
  queued: { label: 'Queued', glyph: '◌', active: true },
  running: { label: 'Computing', glyph: '◔', active: true },
  computed: { label: 'Diff ready', glyph: '◑', active: false },
  awaiting_signoff: { label: 'Awaiting sign-off', glyph: '◑', active: false },
  publishing: { label: 'Publishing', glyph: '◕', active: true },
  published: { label: 'Published', glyph: '●', active: false },
  discarded: { label: 'Discarded', glyph: '✕', active: false },
  failed: { label: 'Failed', glyph: '!', active: false },
};

export const jobStateMeta = (s) => JOB_STATES[s] || { label: s || '—', glyph: '·', active: false };
export const isActiveJob = (job) => !!JOB_STATES[job?.state]?.active;

export const PUBLISHABLE = ['computed', 'awaiting_signoff'];
export const DISCARDABLE = ['queued', 'running', 'computed', 'awaiting_signoff', 'failed'];

export const JOB_FILTERS = [
  { value: 'active', label: 'In progress', states: ['queued', 'running', 'publishing'] },
  { value: 'review', label: 'To publish', states: ['computed', 'awaiting_signoff'] },
  { value: 'done', label: 'Done', states: ['published', 'discarded', 'failed'] },
  { value: 'all', label: 'All', states: [] },
];

/** 0..1 — partitions when known, else records, published = 1. */
export function progressRatio(job) {
  if (!job) return 0;
  if (['computed', 'awaiting_signoff', 'published'].includes(job.state)) return 1;
  const p = job.progress || {};
  const total = Number(p.partitions_total);
  if (total > 0) return Math.min(1, Number(p.partitions_done || 0) / total);
  return 0;
}

/**
 * Publish = the second user's sign-off. Returns { allowed, reason } so a disabled button
 * can always say why (the database enforces signed_off_by <> requested_by regardless).
 */
export function publishGate({ job, user, canSignOff }) {
  if (!job) return { allowed: false, reason: 'Loading…' };
  if (job.state === 'published') return { allowed: false, reason: 'Already published.' };
  if (job.state === 'discarded')
    return { allowed: false, reason: 'Discarded — history was left untouched.' };
  if (job.state === 'failed')
    return { allowed: false, reason: 'The recompute failed; there is nothing to publish.' };
  if (job.state === 'publishing') return { allowed: false, reason: 'Publishing now.' };
  if (!PUBLISHABLE.includes(job.state))
    return {
      allowed: false,
      reason: 'Still computing — the diff appears when the engine finishes.',
    };
  if (!canSignOff)
    return {
      allowed: false,
      reason: 'Publishing needs the lexistar.recompute.signoff permission.',
    };
  if (isMe(user, job.requested_by))
    return {
      allowed: false,
      reason: `You requested this recompute. A second authorised user must publish it (two-person rule).`,
    };
  return { allowed: true, reason: null };
}

const TRIGGER_LABELS = {
  geofence: 'Geofence',
  route: 'Route',
  norm: 'Fuel norm',
  threshold: 'Threshold',
  declaration: 'Declaration',
  manual: 'Manual',
};

export function triggerLabel(trigger) {
  if (!trigger) return 'Manual';
  const kind = TRIGGER_LABELS[trigger.object_type] || trigger.object_type || 'Manual';
  const name = trigger.name || trigger.object_name;
  const id = trigger.object_id != null && trigger.object_id !== '' ? String(trigger.object_id) : '';
  const shortId = id.length > 12 ? `${id.slice(0, 8)}…` : id;
  const v = trigger.version != null ? ` v${trigger.version}` : '';
  if (trigger.object_type === 'manual' || !id) return `${kind}${name ? ` · ${name}` : ''}`;
  return `${kind} ${name || shortId}${v}`;
}

const dateOnly = (iso) => {
  const s = fmtDateTime(iso);
  return s === '—' ? '…' : s;
};

export function scopeSummary(scope = {}) {
  const parts = [];
  const assets = scope.asset_keys || [];
  const routes = scope.route_ids || [];
  const trips = scope.trip_ids || [];
  if (trips.length) parts.push(`${trips.length} trip${trips.length === 1 ? '' : 's'}`);
  if (routes.length) parts.push(`${routes.length} route${routes.length === 1 ? '' : 's'}`);
  if (assets.length)
    parts.push(
      assets.length <= 2
        ? assets.map((a) => String(a).replace(/^reg:/, '')).join(', ')
        : `${assets.length} trucks`
    );
  if (!parts.length) parts.push('All trucks');
  const from = scope.from || scope.range?.from;
  const to = scope.to || scope.range?.to;
  if (from || to) parts.push(`${dateOnly(from)} → ${dateOnly(to)}`);
  return parts.join(' · ');
}

const signed = (n, unit = '', digits = 0) => {
  const v = Number(n);
  if (n === null || n === undefined || !Number.isFinite(v)) return '—';
  const s = fmtNum(Math.abs(v), digits);
  return `${v > 0 ? '+' : v < 0 ? '−' : '±'}${s}${unit}`;
};

const randSigned = (n) => {
  const v = Number(n);
  if (n === null || n === undefined || !Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : v < 0 ? '−' : '±'}R${fmtNum(Math.abs(v))}`;
};

/** LS-09 §5.19 headline deltas, in the order the spec lists them. */
export function headlineCards(diff) {
  if (!diff) return [];
  const t = diff.trips || {};
  const d = diff.deviations || {};
  return [
    {
      key: 'trips',
      label: 'Trips',
      value: `+${t.added ?? 0} / −${t.removed ?? 0}`,
      hint: `${t.changed ?? 0} changed`,
    },
    { key: 'arrivals', label: 'Arrivals moved', value: fmtNum(diff.arrivals_moved ?? 0) },
    { key: 'departures', label: 'Departures moved', value: fmtNum(diff.departures_moved ?? 0) },
    { key: 'deviations', label: 'Deviations', value: `+${d.added ?? 0} / −${d.removed ?? 0}` },
    {
      key: 'off_route',
      label: 'Off-route distance',
      value: signed(diff.off_route_km_delta, ' km', 1),
    },
    { key: 'fuel', label: 'Fuel variance', value: signed(diff.fuel_variance_l_delta, ' L') },
    { key: 'cost', label: 'Cost', value: randSigned(diff.cost_zar_delta) },
    {
      key: 'signed_off',
      label: 'Signed-off trips affected',
      value: fmtNum(diff.signed_off_trips_affected ?? 0),
      warn: Number(diff.signed_off_trips_affected) > 0,
      hint: Number(diff.signed_off_trips_affected) > 0 ? 'become amendment requests' : 'none',
    },
  ];
}

/** Diff response → { rows (not signed off), signedOff, pagination, tables }. */
export function splitDiffRows(res) {
  const pick = (...keys) => {
    for (const k of keys) if (Array.isArray(res?.[k])) return res[k];
    return null;
  };
  const all = pick('rows', 'diff_rows', 'items', 'data') || (Array.isArray(res) ? res : []);
  const separate = pick('signed_off', 'signed_off_rows', 'amendments', 'proposed_amendments');
  const rows = all.filter((r) => !r.signed_off);
  const signedOff = separate || all.filter((r) => r.signed_off);
  let tables = [];
  if (Array.isArray(res?.tables)) {
    tables = res.tables.map((t) => (typeof t === 'string' ? { table_name: t } : t));
  } else if (res?.tables && typeof res.tables === 'object') {
    tables = Object.entries(res.tables).map(([table_name, count]) => ({ table_name, count }));
  }
  return { rows, signedOff, pagination: res?.pagination || null, tables };
}

const SKIP_FIELDS = new Set([
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
  'version',
  'job_id',
  'tenant_id',
]);

/**
 * Field-by-field comparison for one diff row, changed fields first:
 * [{ field, before, after, changed }].
 */
export function diffFields(row) {
  const before = row?.before || {};
  const after = row?.after || {};
  const changedSet = new Set(Array.isArray(row?.fields_changed) ? row.fields_changed : []);
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (k) => !SKIP_FIELDS.has(k)
  );
  const fields = keys.map((field) => {
    const b = before[field];
    const a = after[field];
    const changed =
      changedSet.size > 0
        ? changedSet.has(field)
        : row?.change === 'changed' && JSON.stringify(b) !== JSON.stringify(a);
    return { field, before: b, after: a, changed };
  });
  return fields.sort((x, y) => Number(y.changed) - Number(x.changed));
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** Display form of a diff value. */
export function formatDiffValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  // Up to 6 decimals, trailing zeros dropped: a moved coordinate (-25.9 → -25.902)
  // must not round to the same text on both sides of the diff.
  if (typeof v === 'number') return v.toLocaleString('en-GB', { maximumFractionDigits: 6 });
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'string') return ISO_RE.test(v) ? fmtDateTime(v) : v;
  if (Array.isArray(v))
    return v.length
      ? v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')
      : '—';
  return JSON.stringify(v);
}

const coord = (o) => {
  if (!o) return null;
  const rawLon = o.lon ?? o.lng ?? o.longitude;
  const rawLat = o.lat ?? o.latitude;
  if (rawLon == null || rawLat == null) return null;
  const lon = Number(rawLon);
  const lat = Number(rawLat);
  return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null;
};

/** 'arrival' | 'departure' | 'other' for an event/visit diff row. */
export function movementKind(row) {
  const type = String(row?.after?.type || row?.before?.type || '').toUpperCase();
  const f = new Set(row?.fields_changed || []);
  if (/ARRIVED|ENTERED|ENTRY/.test(type) || f.has('entered_at')) return 'arrival';
  if (/DEPARTED|EXITED|EXIT/.test(type) || f.has('exited_at')) return 'departure';
  return 'other';
}

/** Moved arrivals/departures with coordinates, for the diff map. */
export function movedPoints(rows) {
  return (rows || [])
    .filter((r) => ['events', 'visits'].includes(r.table_name) && r.change === 'changed')
    .map((r) => ({
      id: r.id ?? r.subject_id,
      subject_id: r.subject_id,
      kind: movementKind(r),
      type: r.after?.type || r.before?.type || r.table_name,
      before: coord(r.before),
      after: coord(r.after),
      beforeAt: r.before?.event_time || r.before?.entered_at || r.before?.exited_at || null,
      afterAt: r.after?.event_time || r.after?.entered_at || r.after?.exited_at || null,
    }))
    .filter((p) => p.kind !== 'other' && (p.before || p.after));
}

export const CHANGE_META = {
  added: {
    label: 'Added',
    glyph: '+',
    className: 'bg-surface text-slate-900 ring-1 ring-inset ring-slate-400',
  },
  changed: { label: 'Changed', glyph: '~', className: 'bg-slate-100 text-slate-800' },
  removed: {
    label: 'Removed',
    glyph: '−',
    className: 'bg-surface text-slate-500 ring-1 ring-inset ring-slate-300 line-through',
  },
};

export const TABLE_LABELS = {
  events: 'Events',
  visits: 'Visits',
  trips: 'Trips',
  trip_legs: 'Trip legs',
  deviations: 'Deviations',
  ticket_matches: 'Ticket matches',
  presence_verifications: 'Presence checks',
  refuel_events: 'Refuels',
  status_current: 'Current status',
};
